<template>
  <div class="grid grid-cols-3 gap-2">
    <InfoLink
      v-for="(link, index) in filteredInfoLinks"
      :key="index"
      :label="link.label"
      :classes="link.classes"
      :is-loading="link.isLoading"
      @onButtonAction="link.action"
    >
      <template #icon>
        <span v-if="link.badgeText" class="cashback-badge">{{
          link.badgeText
        }}</span>
        <img
          v-else
          :src="link.icon"
          width="40"
          height="40"
          alt=""
          role="presentation"
        />
      </template>
    </InfoLink>
    <Teleport to="body">
      <CashbackInfoBottomSheet :name="cashbackInfoBsName" />
    </Teleport>
    <div v-if="isMiniAppLoading" class="fixed left-0 top-0 z-50">
      <FullScreenLoader />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';
  import { controllerModel } from 'shared';
  import {
    App,
    appNavLinks,
    FlowType,
    FullScreenLoader,
    amplitudeLib,
  } from 'freedom-shared';

  import {
    CashbackInfoBottomSheet,
    useFreedomCashbackDetails,
  } from '@/entities/casbhack-info';

  import { analyticsEventsLib } from '@/shared/lib';

  import InfoLink from './InfoLink.vue';

  const infoTicketsIcon = new URL(
    '@/assets/images/navigation/info-tickets.svg',
    import.meta.url,
  ).href;
  const infoFavoritesIcon = new URL(
    '@/assets/images/navigation/info-favorites.svg',
    import.meta.url,
  ).href;
  const infoShieldIcon = new URL(
    '@/assets/images/freedom-blue-shield.svg',
    import.meta.url,
  ).href;

  const { t } = useI18n();
  const router = useRouter();

  const freedomCashbackDetails = useFreedomCashbackDetails();
  const bottomSheet = controllerModel.useBottomSheet();
  const cashbackInfoBsName = 'casbhack-info-bottom-sheet';
  const isMiniAppLoading = ref(false);
  const TIMEOUT_FOR_LOADER_DISAPPEAR = 2000;

  const onStartLoading = () => {
    isMiniAppLoading.value = true;
    setTimeout(() => {
      isMiniAppLoading.value = false;
    }, TIMEOUT_FOR_LOADER_DISAPPEAR);
  };

  const handleTabbarItemClick = (miniAppName: App) => {
    onStartLoading();

    switch (miniAppName) {
      case App.CABINET:
        appNavLinks.openCabinet(
          FlowType.AVIA,
          `?source=${FlowType.AVIA}_main_page`,
        );
        break;
      case App.CUSTOMER_SUPPORT:
        appNavLinks.openCabinet(
          'customer-support',
          `?source=${FlowType.AVIA}_main_page`,
        );
        break;
    }
  };

  const isDarkTheme = document.documentElement.classList.contains('dark');

  const cashbackButtonClass = `rounded-2xl border border-[#01d48a] shadow-[0px_5px_20px_rgba(0,0,0,0.05)] ${
    isDarkTheme ? 'cashback-dark-gradient' : 'cashback-light-gradient'
  }`;

  const infoLinks = computed(() => [
    {
      label: t('details.cashbackTitle'),
      badgeText: `${freedomCashbackDetails.totalCashback.value}%`,
      icon: null,
      classes: {
        button: cashbackButtonClass,
        label: 'cashback-gradient-text',
      },
      dataQaId: 'main.buttonCarousel.cashback',
      action: () => bottomSheet.show(cashbackInfoBsName),
      isLoading: freedomCashbackDetails.isLoading.value,
      isError:
        freedomCashbackDetails.isError.value ||
        !freedomCashbackDetails.currentLoyaltyLevel.value,
    },
    {
      label: t('details.myTickets'),
      badgeText: null,
      icon: infoTicketsIcon,
      classes: {
        button: 'rounded-2xl bg-background-neutral-secondary',
        label: 'text-text-neutral-primary',
      },
      dataQaId: 'main.buttonCarousel.myTickets',
      action: async () => {
        analyticsEventsLib.myTicketsPageOpen('main_page');
        await amplitudeLib.flushEvents();
        handleTabbarItemClick(App.CABINET);
      },
      isLoading: false,
      isError: false,
    },
    {
      label: t('details.favorites'),
      badgeText: null,
      icon: infoFavoritesIcon,
      classes: {
        button: 'rounded-2xl bg-background-neutral-secondary',
        label: 'text-text-neutral-primary',
      },
      dataQaId: 'main.buttonCarousel.favorites',
      action: () => {
        router.push({ name: 'FavoritesPage', query: { source: 'main_page' } });
      },
      isLoading: false,
      isError: false,
    },
  ]);

  const filteredInfoLinks = computed(() =>
    infoLinks.value.filter(link => !link.isError),
  );
</script>

<style scoped>
  .cashback-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 9999px;
    background: linear-gradient(
      255deg,
      rgb(1, 212, 138) 1.12%,
      rgb(0, 111, 194) 101.31%
    );
    color: white;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    white-space: nowrap;
  }
</style>
